import { useContext, useEffect, useMemo, useState } from 'react';
import {
  Box, Divider, FormControl, FormControlLabel, InputLabel, MenuItem, Radio, RadioGroup,
  Select, Stack, TextField, Typography,
} from '@mui/material';
import { HexColorPicker } from 'react-colorful';
import { DialogWrapper } from '../../presentation-components/display-elements-components/dialog-wrapper';
import { CustomFilter } from '../../presentation-components/miscellaneous-components/custom-filter';
import {
  dashboardWidgetFilterConfig, filterValuesToWidgetFilters, widgetFiltersToFilterValues,
} from '../../presentation-components/dashboard-components/dashboard-filter-utils';
import { UserNotificationsContext } from '../../../context/user-notifications/user-notifications';
import DashboardElementService from '../../../shared/services/dashboard-element/dashboard-element.service';
import {
  IDashboardElement, IPostDashboardElement, WidgetAggregation, WidgetMetric, WidgetOverflow, WidgetType, WidgetVisibility,
} from '../../../shared/services/dashboard/dashboard.model';

const WIDGET_TYPES: { value: WidgetType, label: string }[] = [
  { value: 'counter', label: 'Counter / number tile' },
  { value: 'doughnut', label: 'Doughnut chart' },
  { value: 'bar', label: 'Bar chart' },
  { value: 'stacked', label: 'Stacked bar (over time)' },
  { value: 'line', label: 'Line chart (over time)' },
  { value: 'table', label: 'Table' },
  { value: 'text', label: 'Text block' },
];

const METRICS: { value: WidgetMetric, label: string }[] = [
  { value: 'emissions', label: 'Emissions (tCO₂e)' },
  { value: 'count', label: 'Record count' },
  { value: 'energy', label: 'Energy (kWh)' },
  { value: 'spend', label: 'Spend' },
];

const DIMENSIONS: { value: string, label: string }[] = [
  { value: 'scope', label: 'Scope' },
  { value: 'group', label: 'Group' },
  { value: 'category', label: 'Category' },
  { value: 'categoryGroup', label: 'Category Group' },
  { value: 'location', label: 'Location' },
  { value: 'country', label: 'Country' },
  { value: 'supplier', label: 'Supplier' },
  { value: 'project', label: 'Project' },
  { value: 'subsidiary', label: 'Subsidiary' },
  { value: 'type', label: 'Data Type' },
];

const CHART_TYPES: WidgetType[] = ['doughnut', 'bar', 'stacked', 'line'];

// Counters, text blocks and tables let the user choose how oversized content behaves. Counters
// default to scaling (the number fills the tile); text and tables default to scrolling.
const OVERFLOW_CHOICE_TYPES: WidgetType[] = ['counter', 'text', 'table'];
const defaultOverflowFor = (type: WidgetType): WidgetOverflow => (type === 'counter' ? 'scale' : 'scroll');

interface DashboardElementDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  element?: IDashboardElement; // present when editing
}

const dashboardElementService = new DashboardElementService();

export const DashboardElementDialog: React.FC<DashboardElementDialogProps> = ({ open, onClose, onSuccess, element }) => {
  const userNotificationsContext = useContext(UserNotificationsContext);

  const [name, setName] = useState('');
  const [type, setType] = useState<WidgetType>('counter');
  const [metric, setMetric] = useState<WidgetMetric>('emissions');
  const [aggregation, setAggregation] = useState<WidgetAggregation>('sum');
  const [comparison, setComparison] = useState<'none' | 'priorYear'>('none');
  const [dimension, setDimension] = useState<string>('category');
  const [filterValues, setFilterValues] = useState<Record<string, any>>({});
  const [visibility, setVisibility] = useState<WidgetVisibility>('private');
  const [overflow, setOverflow] = useState<WidgetOverflow>('scroll');
  const [color, setColor] = useState<string>('');
  const [copy, setCopy] = useState<string>('');
  const [text, setText] = useState<string>('');
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isChart = CHART_TYPES.includes(type);
  const needsDimension = isChart || type === 'table';
  const isCounter = type === 'counter';
  const isText = type === 'text';

  // Seed from the element on open (edit), or reset to defaults (create).
  useEffect(() => {
    if (!open) return;
    if (element) {
      setName(element.name || '');
      setType(element.type);
      setMetric((element.metric as WidgetMetric) || 'emissions');
      setAggregation(element.config?.aggregation || 'sum');
      setComparison(element.config?.comparison?.mode === 'priorYear' ? 'priorYear' : 'none');
      setDimension(element.dimension || 'category');
      setFilterValues(widgetFiltersToFilterValues(element.config?.filters));
      setVisibility(element.visibility || 'private');
      setOverflow(element.config?.overflow || defaultOverflowFor(element.type));
      setColor(element.style?.color || '');
      setCopy(element.style?.copy || '');
      setText(element.style?.text || '');
    } else {
      setName('');
      setType('counter');
      setMetric('emissions');
      setAggregation('sum');
      setComparison('none');
      setDimension('category');
      setFilterValues({});
      setVisibility('private');
      setOverflow(defaultOverflowFor('counter'));
      setColor('');
      setCopy('');
      setText('');
    }
    setShowColorPicker(false);
  }, [open, element]);

  const isDisabled = useMemo(() => {
    if (!name.trim()) return true;
    if (needsDimension && !dimension) return true;
    if (isCounter && !metric) return true;
    return false;
  }, [name, needsDimension, dimension, isCounter, metric]);

  const handleClose = () => {
    onClose();
  };

  const handleSubmit = async () => {
    const payload: IPostDashboardElement = {
      name: name.trim(),
      type,
      metric: isCounter ? metric : null,
      dimension: needsDimension ? dimension : null,
      config: {
        filters: isText ? undefined : filterValuesToWidgetFilters(filterValues),
        aggregation: isCounter ? aggregation : undefined,
        comparison: isCounter ? { mode: comparison } : undefined,
        // Counters, text and tables carry an overflow choice; charts always scale to fit.
        overflow: OVERFLOW_CHOICE_TYPES.includes(type) ? overflow : undefined,
      },
      style: {
        color: color || undefined,
        copy: copy || undefined,
        text: isText ? text : undefined,
      },
      visibility,
    };
    try {
      setIsSubmitting(true);
      if (element) {
        await dashboardElementService.edit(element.id, payload);
      } else {
        await dashboardElementService.create(payload);
      }
      onSuccess();
      handleClose();
    } catch (e: any) {
      userNotificationsContext.openSnackbar({
        open: true,
        message: e?.response?.data?.message || 'Something went wrong',
        backgroundColor: '#70787A',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <DialogWrapper
      open={open}
      onClose={handleClose}
      title={element ? 'Edit element' : 'New element'}
      cancelHandler={handleClose}
      submitHandler={handleSubmit}
      isSubmitting={isSubmitting}
      disableSaveButton={isDisabled}
      maxWidth="sm"
    >
      <Stack spacing={2} sx={{ pt: 1 }}>
        <TextField
          label="Name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          fullWidth
          size="small"
        />

        <FormControl fullWidth size="small">
          <InputLabel id="widget-type-label">Widget type</InputLabel>
          <Select
            labelId="widget-type-label"
            label="Widget type"
            value={type}
            onChange={(e) => {
              const nextType = e.target.value as WidgetType;
              setType(nextType);
              setOverflow(defaultOverflowFor(nextType));
            }}
          >
            {WIDGET_TYPES.map((t) => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
          </Select>
        </FormControl>

        {isCounter && (
          <Stack spacing={2}>
            <FormControl fullWidth size="small">
              <InputLabel id="metric-label">Metric</InputLabel>
              <Select labelId="metric-label" label="Metric" value={metric} onChange={(e) => setMetric(e.target.value as WidgetMetric)}>
                {METRICS.map((m) => <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>)}
              </Select>
            </FormControl>
            <FormControl fullWidth size="small">
              <InputLabel id="aggregation-label">Aggregation</InputLabel>
              <Select labelId="aggregation-label" label="Aggregation" value={aggregation} onChange={(e) => setAggregation(e.target.value as WidgetAggregation)}>
                <MenuItem value="sum">Total</MenuItem>
                <MenuItem value="average">Average per record</MenuItem>
              </Select>
            </FormControl>
            <FormControl fullWidth size="small">
              <InputLabel id="comparison-label">Comparison</InputLabel>
              <Select labelId="comparison-label" label="Comparison" value={comparison} onChange={(e) => setComparison(e.target.value as 'none' | 'priorYear')}>
                <MenuItem value="none">None</MenuItem>
                <MenuItem value="priorYear">Versus prior year</MenuItem>
              </Select>
            </FormControl>
          </Stack>
        )}

        {needsDimension && (
          <FormControl fullWidth size="small">
            <InputLabel id="dimension-label">Break down by</InputLabel>
            <Select labelId="dimension-label" label="Break down by" value={dimension} onChange={(e) => setDimension(e.target.value)}>
              {DIMENSIONS.map((d) => <MenuItem key={d.value} value={d.value}>{d.label}</MenuItem>)}
            </Select>
          </FormControl>
        )}

        {isText && (
          <TextField
            label="Text"
            value={text}
            onChange={(e) => setText(e.target.value)}
            fullWidth
            multiline
            minRows={3}
            size="small"
          />
        )}

        {!isText && (
          <TextField
            label="Subtitle / note (optional)"
            value={copy}
            onChange={(e) => setCopy(e.target.value)}
            fullWidth
            size="small"
          />
        )}

        {!isText && (
          <Box>
            <Typography variant="body2" sx={{ mb: 1, color: 'text.secondary' }}>Filters (optional)</Typography>
            <CustomFilter
              filters={dashboardWidgetFilterConfig}
              filterValues={filterValues}
              onFilterChange={setFilterValues}
            />
          </Box>
        )}

        <Divider />

        <Box>
          <Stack direction="row" alignItems="center" spacing={2}>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>Colour (optional)</Typography>
            <Box
              onClick={() => setShowColorPicker((s) => !s)}
              sx={{
                width: 28, height: 28, borderRadius: '6px', cursor: 'pointer',
                border: '2px solid', borderColor: 'lightGrey.light',
                backgroundColor: color || 'transparent',
              }}
            />
            {color && (
              <Typography
                variant="caption"
                sx={{ cursor: 'pointer', color: 'text.secondary' }}
                onClick={() => setColor('')}
              >
                Clear
              </Typography>
            )}
          </Stack>
          {showColorPicker && (
            <Box sx={{ mt: 1 }}>
              <HexColorPicker color={color || '#4caf50'} onChange={setColor} />
            </Box>
          )}
        </Box>

        {/* Counters, text blocks and tables let the user choose how oversized content behaves. */}
        {OVERFLOW_CHOICE_TYPES.includes(type) && (
          <FormControl fullWidth size="small">
            <InputLabel id="overflow-label">If the content is too big</InputLabel>
            <Select
              labelId="overflow-label"
              label="If the content is too big"
              value={overflow}
              onChange={(e) => setOverflow(e.target.value as WidgetOverflow)}
            >
              <MenuItem value="scroll">Make it scrollable</MenuItem>
              <MenuItem value="scale">Scale it to fit</MenuItem>
            </Select>
          </FormControl>
        )}

        <FormControl>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>Visibility</Typography>
          <RadioGroup row value={visibility} onChange={(e) => setVisibility(e.target.value as WidgetVisibility)}>
            <FormControlLabel value="private" control={<Radio />} label="Only me" />
            <FormControlLabel value="organisation" control={<Radio />} label="Share with organisation" />
          </RadioGroup>
        </FormControl>
      </Stack>
    </DialogWrapper>
  );
};
